import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const SignInLayer = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      setErrorMessage("Missing REACT_APP_API_URL in environment.");
      return;
    }

    if (!username || !password) {
      setErrorMessage("Username and password are required.");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(apiUrl + "/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle error responses
        throw new Error(data.message || `Request failed with status ${response.status}`);
      }

      if (data.success && data.token) {
        // Store token and user data in localStorage
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("role", JSON.stringify(data.user.role));

        // Fetch user's page access and navigate to first accessible page
        try {
          const accessResponse = await fetch(`${apiUrl}/role-page-access/my-access`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.token}`,
            },
          });

          const accessData = await accessResponse.json();

          if (accessData.success && accessData.data && accessData.data.length > 0) {
            // Find the first page with can_access = true, sorted by menu_order
            const accessiblePages = accessData.data
              .filter(page => page.can_access === true)
              .sort((a, b) => (a.menu_order || 0) - (b.menu_order || 0));

            if (accessiblePages.length > 0) {
              // Navigate to the first accessible page
              navigate(accessiblePages[0].page_route);
            } else {
              // Fallback: no accessible pages, go to add-asset
              navigate("/add-asset");
            }
          } else {
            // Fallback: if API fails or no pages, use role-based redirect
            const userRole = (data.user.role || 'user').toLowerCase();
            if (userRole === 'admin') {
              navigate("/");
            } else {
              navigate("/add-asset");
            }
          }
        } catch (accessError) {
          console.error('Failed to fetch page access:', accessError);
          // Fallback: use role-based redirect if page access fetch fails
          const userRole = (data.user.role || 'user').toLowerCase();
          if (userRole === 'admin') {
            navigate("/");
          } else {
            navigate("/add-asset");
          }
        }
      } else {
        throw new Error(data.message || "Login failed");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setErrorMessage(err instanceof Error ? err.message : "Unexpected error");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section
      className='auth bg-base d-flex align-items-center justify-content-center'
      style={{ minHeight: '100vh', backgroundImage: 'none' }}
    >
    
      <div className='auth-right py-32 px-24 d-flex flex-column justify-content-center'>
        <div className='max-w-464-px mx-auto w-100' style={{ marginTop: '-50px' }}>
          <div className='text-center'>
              <img src='assets/images/login_s.png' alt='' className='d-block mx-auto max-w-300-px' />
            <h4 className='mb-12'>Asset Registration System</h4>
            <p className='mb-32 text-secondary-light text-lg'>
              Welcome back! please enter your detail
            </p>
          </div>
          <form onSubmit={handleSubmit} noValidate>
            <div className='icon-field mb-16'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='f7:person' />
              </span>
              <input
                type='text'
                className='form-control h-56-px bg-neutral-50 radius-12'
                placeholder='Username or Email'
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className='position-relative mb-20'>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='solar:lock-password-outline' />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className='form-control h-56-px bg-neutral-50 radius-12'
                  id='your-password'
                  placeholder='Password'
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <span
                className='toggle-password cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light'
                onClick={() => setShowPassword(!showPassword)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowPassword(!showPassword);
                  }
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon icon={showPassword ? 'ri:eye-off-line' : 'ri:eye-line'} />
              </span>
            </div>
            
            {errorMessage && (
              <div className='alert alert-danger alert-dismissible fade show d-flex align-items-center gap-2 mb-16' role='alert'>
                <Icon icon='mdi:alert-circle' className='text-xl' />
                <div className='flex-grow-1'>
                  <strong>Error!</strong> {errorMessage}
                </div>
                <button 
                  type='button' 
                  className='btn-close' 
                  onClick={() => setErrorMessage("")}
                  aria-label='Close'
                />
              </div>
            )}

            <button
              type='submit'
              className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32'
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing In..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SignInLayer;
